import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { LoginData } from '@lnr-v2/ui';
import { AuthLayout, LoginForm, ThemeToggle } from '@lnr-v2/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Get the redirect path from location state, default to dashboard
  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    '/admin/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (data: LoginData) => {
    setLoading(true);
    setError(undefined);

    try {
      // Use the auth context login method with rememberMe option
      await login(data.email, data.password, data.rememberMe);

      // Navigation is handled by the useEffect above when user state changes
    } catch (err) {
      console.error('Login error:', err);

      if (err instanceof Error) {
        // Handle specific error messages from API
        const message = err.message.toLowerCase();

        if (
          message.includes('invalid') ||
          message.includes('unauthorized') ||
          message.includes('credentials')
        ) {
          setError('Invalid email or password. Please try again.');
        } else if (
          message.includes('rate limit') ||
          message.includes('too many')
        ) {
          setError('Too many login attempts. Please try again later.');
        } else if (message.includes('network') || message.includes('fetch')) {
          setError(
            'Network error. Please check your connection and try again.'
          );
        } else if (message.includes('not found')) {
          setError('User not found. Please check your email address.');
        } else if (
          message.includes('disabled') ||
          message.includes('inactive')
        ) {
          setError('Your account has been disabled. Please contact support.');
        } else {
          // Use the error message from API if it's meaningful, otherwise use generic
          setError(
            err.message || 'An error occurred during login. Please try again.'
          );
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Don't render the form if user is already authenticated
  if (user) {
    return null;
  }

  return (
    <AuthLayout
      theme={theme}
      themeToggle={<ThemeToggle theme={theme} onToggle={toggleTheme} />}
    >
      <LoginForm onSubmit={handleSubmit} error={error} loading={loading} />
    </AuthLayout>
  );
};
